import {
  ContextualMenuItemType,
  DirectionalHint,
  IButtonProps,
  IconButton,
  Shimmer,
  Stack,
  TooltipHost
} from "@fluentui/react";
import { getNextColor } from "@fluentui/react-charting";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LineChartComponent } from "../../ChartComponents/LineChartFluent";
import { PartnerSubscriptionTrendChartsProps } from "../../interfaces/PartnerView.interfaces";
import CustomCard from "../../SharedComponents/CustomCard";
import {
  classNames,
  contextualMenuStyles,
  customCardChartStyles,
  horizontalBarHeaderShimmerStyles
} from "../../SharedComponents/Styles/CustomCardWithMetrics.styles";
import { selectViewport } from "../../slices/UI.slice";
import { useAppSelector } from "../../store/hooks";

// Helper to parse ISO date string to Date (ignore time)
function parseSnapshotDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  const dateObj = new Date(dateStr);
  if (isNaN(dateObj.getTime())) return null;
  return new Date(new Date(dateObj).toISOString());
}

// Helper to get all unique snapshotDate entries present in the data
function getAvailableMonths(data: any[]): Date[] {
  const dateSet = new Set<string>();
  data.forEach((item) => {
    if (item.snapshotDate) {
      dateSet.add(item.snapshotDate);
    }
  });
  // Sort dates ascending, preserving the exact value from data
  return Array.from(dateSet)
    .map((dateStr) => new Date(dateStr))
    .sort((a, b) => a.getTime() - b.getTime());
}

export const PartnerSubscriptionTrendCharts = ({
  loading,
  metricFilters,
  onDataPointClick,
  onResetFilters,
  filteredData,
  tenantsTrendBySkuFamilyKustoLink,
  totalSeatsTrendBySkuFamilyKustoLink,
  totalPAUTrendBySkuFamilyKustoLink
}: PartnerSubscriptionTrendChartsProps) => {
  const customCardContainerRef = useRef<HTMLDivElement>(null);
  const resizedWidth = useAppSelector((state) => selectViewport(state).offsetWidth);
  const [width, setWidth] = useState<number | undefined>(undefined);

  const chartConfigs = [
    {
      key: "numberOfTenants",
      title: "Trend of distinct Tenant counts By Sku Family",
      yField: "numberOfTenants",
      kustoLink: tenantsTrendBySkuFamilyKustoLink,
      tooltipDescription: "Distinct count of tenants associated with each Sku Family over time"
    },
    {
      key: "totalSeats",
      title: "Trend of Total Seats By Sku Family",
      yField: "totalSeats",
      kustoLink: totalSeatsTrendBySkuFamilyKustoLink,
      tooltipDescription: "Total seats associated with each Sku Family over time"
    },
    {
      key: "totalPAU",
      title: "Trend of Total PAU By Sku Family",
      yField: "totalPAU",
      kustoLink: totalPAUTrendBySkuFamilyKustoLink,
      tooltipDescription: "Total PAU associated with each Sku Family over time"
    }
  ];

  // Only show months available in data
  const availableMonths = useMemo(() => getAvailableMonths(filteredData), [filteredData]);
  const [startIdx] = useState(0);

  // Show all available months, or paginate if needed
  const monthsToShow = availableMonths.length;
  const months = availableMonths.slice(startIdx, startIdx + monthsToShow);

  // Group data by skuFamily and month
  const buildMultiLineChartSeries = (yField: string, data: any[], months: Date[]) => {
    // Get all unique skuFamily
    const skuFamilies = Array.from(new Set(data.map((item) => item.skuFamily).filter(Boolean)));

    // For each skuFamily, build a series
    return skuFamilies.map((skuFamily, idx) => {
      // For each month, sum the yField for that skuFamily
      const seriesData = months.map((month) => {
        const monthStr = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
        const filtered = data.filter((item) => {
          if (item.skuFamily !== skuFamily) return false;
          const dateObj = parseSnapshotDate(item.snapshotDate);
          if (!dateObj) return false;
          const itemMonthStr = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}`;
          return itemMonthStr === monthStr;
        });
        // Sum the yField for this month
        const y = filtered.reduce((sum, item) => sum + (Number(item[yField]) || 0), 0);
        return {
          x: new Date(new Date(month)),
          y,
          onDataPointClick: () => onDataPointClick({ field: "skuFamily", title: skuFamily, value: skuFamily })
        };
      });
      // Use getNextColor for color assignment
      return {
        legend: skuFamily,
        data: seriesData,
        color: getNextColor(idx),
        lineOptions: { lineBorderWidth: "4" }
      };
    });
  };

  const handleResize = useCallback((width: number) => setWidth(width), []);
  const getHighlightedStatus = () => Object.keys(metricFilters).length;
  const horizontalBarHeader = (title: string | undefined, index: number, isLoading: boolean) => {
    const iconButtonProps: IButtonProps = {
      ariaLabel: "Applied Filters",
      menuProps: {
        directionalHint: DirectionalHint.bottomRightEdge,
        items: [
          {
            key: "selectedFiltersHeader",
            itemType: ContextualMenuItemType.Header,
            text: "Selected Filters",
            itemProps: {
              styles: {
                label: `${classNames.layoutText} ${classNames.headerLabel}`
              }
            }
          },
          {
            key: "selectedFiltersContext",
            onRender: (): ReactNode => {
              if (!Object.keys(metricFilters).length) {
                return <></>;
              }
              const formatValue = (value: string) => {
                const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
                if (value && dateRegex.test(value) && !isNaN(new Date(value).getTime())) {
                  return new Date(value).toISOString().substring(0, 10);
                }
                return value;
              };
              return (
                <div className={classNames.appliedFiltersContainer}>
                  {Object.keys(metricFilters)?.map((key) => (
                    <>
                      <div key={1} className={`${classNames.layoutText} ${classNames.nameText}`}>
                        {key}
                      </div>
                      <div className={classNames.essentialsSeparator}>:</div>
                      <div key={2} className={`${classNames.layoutText} ${classNames.secondaryText}`}>
                        {" "}
                        {formatValue(metricFilters[key].toString()) || "(empty)"}
                      </div>
                    </>
                  ))}
                </div>
              );
            }
          }
        ],
        styles: contextualMenuStyles
      },
      title: "Applied Filters"
    };

    if (isLoading) {
      return <Shimmer styles={horizontalBarHeaderShimmerStyles} />;
    }

    return (
      <Stack horizontal styles={{ root: { alignItems: "center", justifyContent: "space-between" } }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>{title}</span>
          {chartConfigs.find((x: any) => x.tooltipDescription?.length > 0) && (
            <TooltipHost
              content={
                chartConfigs.find((x: any) => x.tooltipDescription?.length > 0 && x.title == title)?.tooltipDescription
              }
              id="tooltip"
            >
              <IconButton
                iconProps={{ iconName: "Info" }}
                styles={{
                  root: {
                    marginTop: 5
                  }
                }}
              />
            </TooltipHost>
          )}
        </div>
        {getHighlightedStatus() ? (
          <Stack horizontal>
            <TooltipHost content="Clear all filters by clicking here." calloutProps={{ gapSpace: 0 }}>
              <IconButton
                iconProps={{ iconName: "ClearFilter" }}
                ariaLabel="Clear All Filters"
                onClick={() => {
                  onResetFilters();
                }}
              />
            </TooltipHost>
            <TooltipHost content="Filters already in use" calloutProps={{ gapSpace: 0 }}>
              <IconButton {...iconButtonProps} />
            </TooltipHost>
          </Stack>
        ) : (
          ""
        )}
      </Stack>
    );
  };

  // Memoized chart items
  const itemMemo = useMemo(() => {
    const charts = chartConfigs.map((config) => {
      const chartData = {
        lineChartData: buildMultiLineChartSeries(config.yField, filteredData, months)
      };

      return {
        component: () => (
          <>
            <LineChartComponent
              data={chartData}
              allowResize={true}
              tickValues={months}
              height={300}
              width={width}
              xAxisTitle="Month"
              yAxisTitle={config.title}
              isLoading={loading}
            />
          </>
        ),
        key: config.key,
        title: config.title,
        kustoLink: config.kustoLink
      };
    });

    return charts;
  }, [filteredData, loading, width, months, startIdx, availableMonths.length]);

  // Hooks
  useEffect(() => {
    if (customCardContainerRef.current) {
      handleResize(customCardContainerRef.current?.offsetWidth);
    }
  }, [resizedWidth, customCardContainerRef]);

  return itemMemo?.map(({ component, key, title, kustoLink }, index) => (
    <CustomCard
      key={key}
      cardHeaderTitle={horizontalBarHeader(title, index, loading)}
      styles={customCardChartStyles}
      isLoading={loading}
      kustoLink={kustoLink}
    >
      <div ref={customCardContainerRef} className={classNames.chartContainer}>
        {component()}
      </div>
    </CustomCard>
  ));
};
